import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Alert,
  CircularProgress,
  Grid,
  Divider,
  Tooltip,
  IconButton,
  TextField,
  Card,
  Tabs,
  Tab,
  Chip,
  Switch,
  FormControlLabel
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import RefreshIcon from '@mui/icons-material/Refresh';
import SaveIcon from '@mui/icons-material/Save';
import LockIcon from '@mui/icons-material/Lock';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HeadsetMicIcon from '@mui/icons-material/HeadsetMic';
import VoiceSelector from '../common/VoiceSelector';
import AccentSelector from '../common/AccentSelector';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { getElevenLabsVoices } from '../../services/api';
import apiService from '../../services/api';
import axios from 'axios';
import TextToSpeech from '../TextToSpeech';

// Provider configuration
const PROVIDERS = {
  elevenlabs: {
    name: 'ElevenLabs',
    icon: '🎙️',
    color: '#1a1a1a',
    description: 'Premium neural voices with emotional depth'
  },
  google: {
    name: 'Google Cloud',
    icon: '🔊',
    color: '#4285f4',
    description: 'High-quality neural voices with broad language support'
  },
  azure: {
    name: 'Azure',
    icon: '☁️',
    color: '#0078d4',
    description: 'Microsoft neural voices with natural intonation'
  },
  openai: {
    name: 'OpenAI',
    icon: '🤖',
    color: '#10a37f',
    description: 'Advanced AI voices with clarity'
  }
};

// Available voices per provider
const PROVIDER_VOICES = {
  elevenlabs: [], // Will be loaded dynamically
  google: [
    { voice_id: 'en-GB-Neural2-A', name: 'British Female A (Professional)' },
    { voice_id: 'en-GB-Neural2-F', name: 'British Female F (Warm)' },
    { voice_id: 'en-GB-Neural2-C', name: 'British Female C (Young)' },
    { voice_id: 'en-GB-Neural2-B', name: 'British Male B (Professional)' },
    { voice_id: 'en-GB-Neural2-D', name: 'British Male D (Authoritative)' }
  ],
  azure: [
    { voice_id: 'en-GB-LibbyNeural', name: 'Libby (Empathetic)' },
    { voice_id: 'en-GB-SoniaNeural', name: 'Sonia (Cheerful)' },
    { voice_id: 'en-GB-RyanNeural', name: 'Ryan (Conversational)' }
  ],
  openai: [
    { voice_id: 'alloy', name: 'Alloy (Neutral)' },
    { voice_id: 'nova', name: 'Nova (Friendly)' },
    { voice_id: 'onyx', name: 'Onyx (Authoritative)' },
    { voice_id: 'echo', name: 'Echo (Smooth)' },
    { voice_id: 'fable', name: 'Fable (Expressive)' },
    { voice_id: 'shimmer', name: 'Shimmer (Warm)' }
  ]
};

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`provider-tabpanel-${index}`}
      aria-labelledby={`provider-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const TherapistVoiceSettingsV2 = ({ token }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [preferredProviderModified, setPreferredProviderModified] = useState(false);
  
  // Voice settings state
  const [voiceSettings, setVoiceSettings] = useState({
    providers: {},
    allowedProviders: [],
    preferredProvider: null,
    providerPermissions: {
      canSelectProvider: false,
      costLimit: { daily: 10, monthly: 200 },
      overrideInterfaceProvider: false
    },
    availableProviders: []
  });
  
  // Available voices
  const [availableVoices, setAvailableVoices] = useState(PROVIDER_VOICES);
  
  // Sample text for preview
  const [sampleText, setSampleText] = useState(
    'Hello! This is a sample of how this voice will sound during therapy sessions. How are you feeling today?'
  );
  
  // Track which providers have unsaved changes
  const [modifiedProviders, setModifiedProviders] = useState(new Set());

  // Advanced Voice Mode state
  const [advancedVoiceMode, setAdvancedVoiceMode] = useState({ enabled: false });
  const [avmSaving, setAvmSaving] = useState(false);

  // Fetch voice settings
  const fetchVoiceSettings = useCallback(async () => {
    if (!token) return;
    
    setLoading(true);
    setError('');
    
    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
      
      // Fetch V2 voice settings
      const response = await axios.get(`${API_BASE_URL}/therapist/voice-settings/v2`, {
        headers: { 'x-auth-token': token }
      });
      
      setVoiceSettings(response.data.voiceSettings);
      
      // Load ElevenLabs voices
      try {
        const elevenLabsVoices = await getElevenLabsVoices();
        setAvailableVoices(prev => ({
          ...prev,
          elevenlabs: elevenLabsVoices
        }));
      } catch (err) {
        console.warn('Could not load ElevenLabs voices:', err);
      }
      
      // Also fetch Advanced Voice Mode config
      try {
        const avmConfig = await apiService.getVoiceModeConfig(token);
        if (avmConfig?.therapistSettings) {
          setAdvancedVoiceMode(avmConfig.therapistSettings);
        }
      } catch (avmErr) {
        console.warn('Could not load AVM settings:', avmErr);
      }

    } catch (err) {
      console.error('Error fetching voice settings:', err);
      setError('Failed to load voice settings. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchVoiceSettings();
  }, [fetchVoiceSettings]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleVoiceChange = (provider, field, value) => {
    setVoiceSettings(prev => ({
      ...prev,
      providers: {
        ...prev.providers,
        [provider]: {
          ...prev.providers[provider],
          [field]: value === '' ? null : value
        }
      }
    }));
    
    // Track this provider as modified
    setModifiedProviders(prev => new Set([...prev, provider]));
  };

  const handleSaveProvider = async (provider) => {
    setSaving(true);
    setError('');
    setSuccess('');
    
    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
      
      const providerSettings = voiceSettings.providers[provider] || {};
      
      await axios.put(
        `${API_BASE_URL}/therapist/voice-settings/v2/provider/${provider}`,
        {
          defaultAITherapistVoice: providerSettings.defaultAITherapistVoice,
          defaultHumanTherapistVoice: providerSettings.defaultHumanTherapistVoice,
          defaultClientVoice: providerSettings.defaultClientVoice
        },
        {
          headers: { 'x-auth-token': token }
        }
      );
      
      setSuccess(`${PROVIDERS[provider].name} voice settings saved successfully!`);
      
      // Remove from modified set
      setModifiedProviders(prev => {
        const newSet = new Set(prev);
        newSet.delete(provider);
        return newSet;
      });
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error saving voice settings:', err);
      setError(err.response?.data?.msg || 'Failed to save voice settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSavePreferredProvider = async () => {
    setSaving(true);
    setError('');
    
    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
      
      await axios.put(
        `${API_BASE_URL}/therapist/voice-settings/v2/preferred-provider`,
        {
          preferredProvider: voiceSettings.preferredProvider
        },
        {
          headers: { 'x-auth-token': token }
        }
      );
      
      setSuccess('Preferred provider updated successfully!');
      setPreferredProviderModified(false);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error updating preferred provider:', err);
      setError(err.response?.data?.msg || 'Failed to update preferred provider');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = (provider) => {
    setVoiceSettings(prev => ({
      ...prev,
      providers: {
        ...prev.providers,
        [provider]: {
          defaultAITherapistVoice: null,
          defaultHumanTherapistVoice: null,
          defaultClientVoice: null
        }
      }
    }));
    
    setModifiedProviders(prev => new Set([...prev, provider]));
  };

  const getVoiceName = (voiceId, provider) => {
    if (!voiceId) return 'Use Platform Default';
    const voices = availableVoices[provider] || [];
    const voice = voices.find(v => v.voice_id === voiceId);
    return voice ? voice.name : voiceId;
  };

  const renderProviderTab = (provider) => {
    const isAllowed = voiceSettings.allowedProviders?.includes(provider);
    const providerConfig = PROVIDERS[provider];
    const providerSettings = voiceSettings.providers?.[provider] || {};
    const voices = availableVoices[provider] || [];
    const hasChanges = modifiedProviders.has(provider);
    
    return (
      <Box>
        {!isAllowed && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2">
              You don't have permission to configure {providerConfig.name} voices. 
              Contact your administrator to enable access.
            </Typography>
          </Alert>
        )}
        
        <Paper 
          elevation={0} 
          variant="outlined" 
          sx={{ 
            p: 3, 
            opacity: isAllowed ? 1 : 0.6,
            pointerEvents: isAllowed ? 'auto' : 'none'
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h6">
                {providerConfig.icon} {providerConfig.name} Configuration
              </Typography>
              {hasChanges && (
                <Chip 
                  label="Unsaved Changes" 
                  size="small" 
                  color="warning" 
                />
              )}
            </Box>
            <Box>
              <Tooltip title="Reset to defaults">
                <IconButton onClick={() => handleReset(provider)} disabled={saving} aria-label="Reset to defaults">
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={() => handleSaveProvider(provider)}
                disabled={saving || !hasChanges}
              >
                Save {providerConfig.name}
              </Button>
            </Box>
          </Box>
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {providerConfig.description}
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id={`${provider}-ai-voice-label`}>
                  AI Therapist Voice
                </InputLabel>
                <Select
                  labelId={`${provider}-ai-voice-label`}
                  value={providerSettings.defaultAITherapistVoice || ''}
                  onChange={(e) => handleVoiceChange(provider, 'defaultAITherapistVoice', e.target.value)}
                  label="AI Therapist Voice"
                >
                  <MenuItem value="">
                    <em>Use Platform Default</em>
                  </MenuItem>
                  {voices.map(voice => (
                    <MenuItem key={voice.voice_id} value={voice.voice_id}>
                      {voice.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              {providerSettings.defaultAITherapistVoice && sampleText && (
                <Box sx={{ mt: 1, display: 'flex', alignItems: 'center' }}>
                  <Typography variant="body2" sx={{ mr: 1 }}>Preview:</Typography>
                  <TextToSpeech 
                    text={sampleText} 
                    voiceId={providerSettings.defaultAITherapistVoice} 
                    showVoiceSelector={false} 
                    useStreaming={false} 
                    size="small" 
                  />
                </Box>
              )}
            </Grid>
            
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id={`${provider}-human-voice-label`}>
                  Human Therapist Voice (Your Voice in Transcripts)
                </InputLabel>
                <Select
                  labelId={`${provider}-human-voice-label`}
                  value={providerSettings.defaultHumanTherapistVoice || ''}
                  onChange={(e) => handleVoiceChange(provider, 'defaultHumanTherapistVoice', e.target.value)}
                  label="Human Therapist Voice (Your Voice in Transcripts)"
                >
                  <MenuItem value="">
                    <em>Use Platform Default</em>
                  </MenuItem>
                  {voices.map(voice => (
                    <MenuItem key={voice.voice_id} value={voice.voice_id}>
                      {voice.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              {providerSettings.defaultHumanTherapistVoice && sampleText && (
                <Box sx={{ mt: 1, display: 'flex', alignItems: 'center' }}>
                  <Typography variant="body2" sx={{ mr: 1 }}>Preview:</Typography>
                  <TextToSpeech 
                    text={sampleText} 
                    voiceId={providerSettings.defaultHumanTherapistVoice} 
                    showVoiceSelector={false} 
                    useStreaming={false} 
                    size="small" 
                  />
                </Box>
              )}
            </Grid>
            
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id={`${provider}-client-voice-label`}>
                  Client Voice (Transcript Playback Only)
                </InputLabel>
                <Select
                  labelId={`${provider}-client-voice-label`}
                  value={providerSettings.defaultClientVoice || ''}
                  onChange={(e) => handleVoiceChange(provider, 'defaultClientVoice', e.target.value)}
                  label="Client Voice (Transcript Playback Only)"
                >
                  <MenuItem value="">
                    <em>Use Platform Default</em>
                  </MenuItem>
                  {voices.map(voice => (
                    <MenuItem key={voice.voice_id} value={voice.voice_id}>
                      {voice.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              {providerSettings.defaultClientVoice && sampleText && (
                <Box sx={{ mt: 1, display: 'flex', alignItems: 'center' }}>
                  <Typography variant="body2" sx={{ mr: 1 }}>Preview:</Typography>
                  <TextToSpeech 
                    text={sampleText} 
                    voiceId={providerSettings.defaultClientVoice} 
                    showVoiceSelector={false} 
                    useStreaming={false} 
                    size="small" 
                  />
                </Box>
              )}
            </Grid>
          </Grid>
        </Paper>
      </Box>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Voice Settings (Multi-Provider)
      </Typography>
      
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Configure voices across different TTS providers. Your settings apply to all your clients unless you set specific overrides.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Advanced Voice Mode Toggle */}
      <Card
        variant="outlined"
        sx={{
          p: 2,
          mb: 3,
          border: advancedVoiceMode.enabled ? '2px solid' : '1px solid',
          borderColor: advancedVoiceMode.enabled ? 'secondary.main' : 'divider',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <HeadsetMicIcon color={advancedVoiceMode.enabled ? 'secondary' : 'disabled'} />
            <Box>
              <Typography variant="subtitle1" fontWeight="bold">
                Advanced Voice Mode
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Enable real-time voice conversations for your clients using AI-powered speech.
                When enabled, eligible clients will see a voice mode option in their chat interface.
              </Typography>
            </Box>
          </Box>
          <FormControlLabel
            control={
              <Switch
                checked={advancedVoiceMode.enabled || false}
                onChange={async (e) => {
                  const newEnabled = e.target.checked;
                  setAvmSaving(true);
                  try {
                    await apiService.updateTherapistVoiceMode(token, { enabled: newEnabled });
                    setAdvancedVoiceMode(prev => ({ ...prev, enabled: newEnabled }));
                    setSuccess(newEnabled ? 'Advanced Voice Mode enabled' : 'Advanced Voice Mode disabled');
                    setTimeout(() => setSuccess(''), 3000);
                  } catch (err) {
                    setError('Failed to update voice mode setting');
                  } finally {
                    setAvmSaving(false);
                  }
                }}
                disabled={avmSaving}
                color="secondary"
              />
            }
            label={avmSaving ? <CircularProgress size={16} /> : (advancedVoiceMode.enabled ? 'On' : 'Off')}
            labelPlacement="start"
          />
        </Box>

        {/* Full AVM config — shown when enabled */}
        {advancedVoiceMode.enabled && (
          <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
            {/* Accent / Regional Style */}
            <Typography variant="subtitle2" gutterBottom>Accent / Regional Style</Typography>
            <AccentSelector
              value={advancedVoiceMode.accentPresetId || null}
              onChange={async (presetId) => {
                setAvmSaving(true);
                try {
                  // When changing preset, clear custom guidance unless switching to custom
                  const update = { accentPresetId: presetId };
                  if (presetId !== 'custom') update.accentGuidance = null;
                  await apiService.updateTherapistVoiceMode(token, update);
                  setAdvancedVoiceMode(prev => ({ ...prev, ...update }));
                } catch (err) { setError('Failed to update accent'); }
                finally { setAvmSaving(false); }
              }}
              customGuidance={advancedVoiceMode.accentGuidance || ''}
              onCustomGuidanceChange={async (text) => {
                setAdvancedVoiceMode(prev => ({ ...prev, accentGuidance: text }));
                // Debounce save — only save on blur or after typing stops
                clearTimeout(window._avmAccentTimeout);
                window._avmAccentTimeout = setTimeout(async () => {
                  try {
                    await apiService.updateTherapistVoiceMode(token, { accentGuidance: text || null });
                  } catch (err) { console.warn('Failed to save accent guidance:', err); }
                }, 1000);
              }}
              token={token}
            />

            {/* Default Voice */}
            <Typography variant="subtitle2" gutterBottom sx={{ mt: 3 }}>
              Default Voice for Your Clients
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              This voice is used for all your clients unless you set a per-client override below.
            </Typography>
            <VoiceSelector
              value={advancedVoiceMode.geminiVoicePreset}
              onChange={async (voice) => {
                setAvmSaving(true);
                try {
                  await apiService.updateTherapistVoiceMode(token, { geminiVoicePreset: voice });
                  setAdvancedVoiceMode(prev => ({ ...prev, geminiVoicePreset: voice }));
                  setSuccess(voice ? `Voice set to ${voice}` : 'Using platform default voice');
                  setTimeout(() => setSuccess(''), 3000);
                } catch (err) { setError('Failed to update voice'); }
                finally { setAvmSaving(false); }
              }}
              token={token}
              showPlatformDefault
              accentPresetId={advancedVoiceMode.accentPresetId}
            />

            {/* Per-Client Voice Overrides */}
            <Accordion sx={{ mt: 3 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle2">Per-Client Voice Overrides</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Set a different voice for specific clients. These override your default voice above.
                </Typography>
                {advancedVoiceMode.clientVoiceOverrides &&
                  Object.entries(
                    advancedVoiceMode.clientVoiceOverrides instanceof Map
                      ? Object.fromEntries(advancedVoiceMode.clientVoiceOverrides)
                      : advancedVoiceMode.clientVoiceOverrides || {}
                  ).map(([clientId, voiceName]) => (
                    <Box key={clientId} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Typography variant="body2" sx={{ minWidth: 180 }}>{clientId}</Typography>
                      <VoiceSelector
                        value={voiceName}
                        onChange={async (voice) => {
                          const overrides = advancedVoiceMode.clientVoiceOverrides instanceof Map
                            ? Object.fromEntries(advancedVoiceMode.clientVoiceOverrides)
                            : { ...(advancedVoiceMode.clientVoiceOverrides || {}) };
                          if (voice) { overrides[clientId] = voice; }
                          else { delete overrides[clientId]; }
                          try {
                            await apiService.updateTherapistVoiceMode(token, { clientVoiceOverrides: overrides });
                            setAdvancedVoiceMode(prev => ({ ...prev, clientVoiceOverrides: overrides }));
                          } catch (err) { setError('Failed to update client override'); }
                        }}
                        token={token}
                        showPlatformDefault
                        compact
                        accentPresetId={advancedVoiceMode.accentPresetId}
                      />
                    </Box>
                  ))
                }
                <Typography variant="caption" color="text.secondary">
                  Per-client overrides can also be set from the client review page. Only clients with active overrides appear here.
                </Typography>
              </AccordionDetails>
            </Accordion>
          </Box>
        )}
      </Card>

      {/* Provider Access Info */}
      <Card variant="outlined" sx={{ p: 2, mb: 3, backgroundColor: 'background.default' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography variant="subtitle2">Allowed Providers:</Typography>
              {voiceSettings.allowedProviders?.map(provider => (
                <Chip 
                  key={provider}
                  label={PROVIDERS[provider]?.name || provider}
                  size="small"
                  icon={<CheckCircleIcon />}
                  color="success"
                  variant="outlined"
                />
              ))}
            </Box>
            
            {voiceSettings.providerPermissions?.canSelectProvider ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel>Preferred Provider</InputLabel>
                  <Select
                    value={voiceSettings.preferredProvider || ''}
                    onChange={(e) => {
                      setVoiceSettings(prev => ({
                        ...prev,
                        preferredProvider: e.target.value || null
                      }));
                      setPreferredProviderModified(true);
                    }}
                    label="Preferred Provider"
                  >
                    <MenuItem value="">
                      <em>Use Interface Default</em>
                    </MenuItem>
                    {voiceSettings.allowedProviders?.map(provider => (
                      <MenuItem key={provider} value={provider}>
                        {PROVIDERS[provider]?.name || provider}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {preferredProviderModified && (
                  <Button
                    size="small"
                    variant="contained"
                    onClick={handleSavePreferredProvider}
                    disabled={saving}
                    startIcon={<SaveIcon />}
                  >
                    Save
                  </Button>
                )}
              </Box>
            ) : (
              <Typography variant="caption" color="text.secondary">
                <LockIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                Provider selection is managed by your administrator
              </Typography>
            )}
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Typography variant="caption" display="block" color="text.secondary">
              Daily limit: ${voiceSettings.providerPermissions?.costLimit?.daily || 10}
            </Typography>
            <Typography variant="caption" display="block" color="text.secondary">
              Monthly limit: ${voiceSettings.providerPermissions?.costLimit?.monthly || 200}
            </Typography>
          </Grid>
        </Grid>
      </Card>

      {/* Sample Text for Preview */}
      <Card variant="outlined" sx={{ p: 2, mb: 3, backgroundColor: 'background.default' }}>
        <Typography variant="subtitle2" gutterBottom>
          Sample Text for Voice Preview
        </Typography>
        <TextField 
          fullWidth 
          multiline 
          rows={2}
          value={sampleText} 
          onChange={(e) => setSampleText(e.target.value)} 
          placeholder="Type text here to preview how it sounds with each voice"
          variant="outlined"
          size="small"
        />
      </Card>

      {/* Provider Tabs */}
      <Paper elevation={0} variant="outlined">
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs 
            value={activeTab} 
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
          >
            {['elevenlabs', 'google', 'azure', 'openai'].map((provider, index) => {
              const isAllowed = voiceSettings.allowedProviders?.includes(provider);
              const hasChanges = modifiedProviders.has(provider);
              
              return (
                <Tab 
                  key={provider}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <span>{PROVIDERS[provider].icon}</span>
                      <span>{PROVIDERS[provider].name}</span>
                      {!isAllowed && <LockIcon fontSize="small" />}
                      {hasChanges && <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'warning.main', ml: 0.5 }} />}
                    </Box>
                  }
                  id={`provider-tab-${index}`}
                  aria-controls={`provider-tabpanel-${index}`}
                />
              );
            })}
          </Tabs>
        </Box>
        
        {['elevenlabs', 'google', 'azure', 'openai'].map((provider, index) => (
          <TabPanel key={provider} value={activeTab} index={index}>
            {renderProviderTab(provider)}
          </TabPanel>
        ))}
      </Paper>
      
      <Box sx={{ mt: 3, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <InfoIcon fontSize="small" />
          <Typography variant="body2">
            Voice settings apply to all your clients by default. You can set client-specific overrides in each client's settings.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default TherapistVoiceSettingsV2;